// UI gating helpers for the permission model — see
// docs/developer/13-authorization.md.
//
// These read the role-expanded permissions the backend computes in
// `_public()` (server.py): `user.effective_permissions` for the global/MHQ
// set and `user.dealer_effective_permissions[dealerId]` per dealer. The
// frontend deliberately does NOT keep its own copy of the role -> permission
// table: that duplication is exactly how seven files drifted apart under the
// old role-array model (App.js and Shell.jsx were missing MARKET_DNS_MANAGER
// from their MHQ_ROLES while lib/permissions.js had it).
//
// The backend is always the actual enforcement point. Everything here gates
// nav items, buttons and route redirects only.

// Role CODES are still needed in a few places — showing a badge, or the
// Users page's assignment grid. Those come from GET /api/roles at runtime
// (see useRoleCatalogue), never from a constant duplicated here.

export const PERM = {
  DEALER_READ: "dealer.read",
  MAIN_CONTACT_EDIT: "dealer.main_contact.edit",
  HOST_NAME_EDIT: "dealer.host_name.edit",
  CERTIFICATIONS_EDIT: "dealer.certifications.edit",
  LEGAL_PAGES_EDIT: "dealer.legal_pages.edit",
  BRANCH_EDIT: "dealer.branch.edit",
  BRANCH_COORDINATES_EDIT: "dealer.branch.coordinates.edit",
  SERVICES_EDIT: "dealer.services.edit",
  SERVICES_MANAGE: "dealer.services.manage",
  EMPLOYEES_MANAGE: "dealer.employees.manage",
  SOCIAL_MEDIA_EDIT: "dealer.social_media.edit",
  CONTENT_MANAGE: "dealer.content.manage",
  CUSTOM_DATA_EDIT: "dealer.custom_data.edit",
  INTERNAL_CONTACT_EDIT: "dealer.internal_contact.edit",
  LOGO_EDIT: "dealer.logo.edit",
  SITE_STATUS_TOGGLE: "dealer.site_status.toggle",
  SITE_DEPLOY: "dealer.site.deploy",
  SAD_CONFIRMATIONS_EDIT: "dealer.sad_confirmations.edit",
  MD_HASH_VIEW: "dealer.md_hash.view",
  DDM_MIGRATION_MANAGE: "dealer.ddm_migration.manage",
  COOKIE_CONSENTS_MANAGE: "dealer.cookie_consents.manage",
  DNS_MANAGE: "dealer.dns.manage",
  USERS_MANAGE: "users.manage",

  NETWORK_DEALERS_READ: "network.dealers.read",
  NETWORK_IMPERSONATE: "network.impersonate",
  LEGAL_TEMPLATES_MANAGE: "network.legal_templates.manage",
  ACTIVATIONS_MANAGE: "activations.manage",
  IMPORTS_RUN: "imports.run",
  IMPORTS_ARCHIVES_DELETE: "imports.archives.delete",
  SYSTEM_SETTINGS_MANAGE: "system.settings.manage",
  SYSTEM_BRANDING_MANAGE: "system.branding.manage",
  SYSTEM_API_TOKENS_MANAGE: "system.api_tokens.manage",
  SYSTEM_AUDIT_READ: "system.audit.read",
  SYSTEM_DEV_TOOLS: "system.dev_tools",
  SYSTEM_RESTART: "system.restart",
  DEPARTMENTS_MANAGE: "system.departments.manage",
  SYSTEM_DEALER_HOST_SUFFIX_MANAGE: "system.dealer_host_suffix.manage",
  SYSTEM_KEYCLOAK_MANAGE: "system.keycloak.manage",
  SITE_ASSIGN_EXISTING: "system.site.assign_existing",
  SYSTEM_MD_HASH_MANAGE: "system.md_hash.manage",
};

// An MHQ-scoped user holds global roles and no dealer grants; a Dealer-scoped
// user is the reverse. Used to pick between the MHQ and dealer variants of a
// screen, never as an authorization decision on its own.
export function isMhqUser(user) {
  return !!(user?.roles?.length && !user?.dealer_access?.length);
}

// Permissions that apply everywhere: the system, and (for an MHQ user) every
// dealer. Always empty for a Dealer-scoped user.
export function hasGlobalPermission(user, permission) {
  return !!user?.effective_permissions?.includes(permission);
}

// This user's effective permissions for one dealer. For an MHQ user that's
// their global set (their roles are not dealer-specific); for a Dealer user
// it's the expansion of the roles on that dealer's grant alone.
export function permissionsFor(user, dealerId) {
  const global = user?.effective_permissions || [];
  if (!dealerId) return global;
  const perDealer = user?.dealer_effective_permissions?.[dealerId] || [];
  return global.length ? [...new Set([...global, ...perDealer])] : perDealer;
}

export function hasPermission(user, dealerId, permission) {
  if (hasGlobalPermission(user, permission)) return true;
  return !!user?.dealer_effective_permissions?.[dealerId]?.includes(permission);
}

// "ALL" (every dealer, via a global grant) or the array of dealer ids where
// this user holds `permission`.
export function dealerIdsWithPermission(user, permission) {
  if (hasGlobalPermission(user, permission)) return "ALL";
  const byDealer = user?.dealer_effective_permissions || {};
  return Object.keys(byDealer).filter((id) => byDealer[id]?.includes(permission));
}

// Whether the user holds `permission` globally or on at least one dealer —
// for gating a nav item or route that isn't about one specific dealer yet.
export function hasPermissionAnywhere(user, permission) {
  const ids = dealerIdsWithPermission(user, permission);
  return ids === "ALL" || ids.length > 0;
}

// --- User management (kept as named helpers: every /users call site reads
// better for them, and they're the most-used pair in the app) ---

export function hasGlobalUserManagement(user) {
  return hasGlobalPermission(user, PERM.USERS_MANAGE);
}

export function manageableDealerIds(user) {
  return dealerIdsWithPermission(user, PERM.USERS_MANAGE);
}

export function hasAnyUserManagement(user) {
  return hasPermissionAnywhere(user, PERM.USERS_MANAGE);
}
